import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// >>> ADDED: decodes a JWT payload without pulling in a library. Used to
// read the "Role" claim off Token 2 — the API doc confirms Role lives in
// the JWT claims, not necessarily in the response body.
function decodeJwt(token) {
  try {
    const clean = token.replace(/^Bearer\s+/i, '');
    const payload = clean.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// Client code(s) allowed to log in as waralsadmin. REPLACE with your real value(s).
const WARALS_ADMIN_CLIENT_CODES = ['WARALSADMIN'];

const LogIn = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  let navigate = useNavigate();

  const [formData, setFormData] = useState({
    userId: "",
    password: "",
  });
  const [schoolData, setSchoolData] = useState([]);

    const fetchSchoolDetails = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching school details: ${response.status}`);
      }
      const data = await response.json();
      setSchoolData(data);
    } catch (error) {
      console.error(error);
    }
  };

  // useEffect(() => {
  //   fetchSchoolDetails();
  // }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

    const fetchTeacherDetails = async (classId, sectionId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/CreateClass/FillClassSectionDetails?Id=${classId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch teacher details: ${response.status}`);
      }
      const data = await response.json();
      const section = data.find((s) => s.sectionId === sectionId);
      if (section) {
        sessionStorage.setItem('teacherId', section.teacherId);
        sessionStorage.setItem('teacherName', section.teacherName);
      } else {
        console.warn("Teacher not found for the given section.");
      }
    } catch (error) {
      console.error("Error fetching teacher details:", error);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

 const handlelogIn = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    // Client code decides the API: waralsadmin -> UniversalLogin, otherwise -> UserLogin
    const clientCode = (sessionStorage.getItem('clientCode') || '').trim().toLowerCase();
    const isWaralsAdmin = clientCode === 'waralsadmin';
    console.log('clientCode:', clientCode, '| UniversalLogin:', isWaralsAdmin); // remove after testing

    const apiUrl = process.env.REACT_APP_BASE_URL;
    const loginEndpoint = isWaralsAdmin
      ? `${apiUrl}/Default/UniversalLogin`
      : `${apiUrl}/UserLogin`;

    const response = await fetch(loginEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": sessionStorage.getItem('token1')
      },
      body: JSON.stringify({
        userId: formData.userId,
        password: formData.password,
      }),
    });

    if (response.ok) {
      const responseData = await response.json();
      const { token, refreshToken, lastLoginOn } = responseData;

      // The two APIs return the user object under different keys
      const rawUser = isWaralsAdmin ? responseData.adminUser : responseData.objOutput;

      if (!token || !rawUser || typeof rawUser !== 'object') {
        console.error('Unexpected login response shape:', responseData);
        setSnackbarMessage('Login succeeded but the response format was unexpected.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        return;
      }

      const leftDays = isWaralsAdmin ? responseData.leftDays : rawUser.leftDays;
      const user = isWaralsAdmin
        ? {
            userId: rawUser.userID,
            classId: rawUser.classId,
            sectionId: rawUser.sectionId,
            employeeId: rawUser.employeeID,
            userGroupId: rawUser.userGroupID,
            userType: rawUser.userType,
            organizationName: rawUser.organizationName,
            logo: rawUser.logo,
            imageUrl: rawUser.imageUrl,
            clientName: rawUser.userName,
            expiryDate: rawUser.expiryDate,
            branchId: rawUser.locationID,
            is_Master: rawUser.is_Master,
          }
        : {
            userId: rawUser.userId,
            classId: rawUser.classId,
            sectionId: rawUser.sectionId,
            employeeId: rawUser.employeeId,
            userGroupId: rawUser.userGrpId,
            userType: rawUser.userType,
            organizationName: rawUser.organizationName,
            logo: rawUser.logo,
            imageUrl: rawUser.imageUrl,
            clientName: rawUser.employeeName,
            expiryDate: rawUser.expiryDate,
            branchId: rawUser.branchId,
            is_Master: rawUser.is_Master,
          };

      sessionStorage.setItem('login', true);
      sessionStorage.setItem('token', token);
      if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
      if (lastLoginOn != null) sessionStorage.setItem('lastLoginOn', lastLoginOn);
      if (leftDays != null) sessionStorage.setItem('leftday', leftDays);

      if (isWaralsAdmin) {
        const claims = decodeJwt(token);
        const role = claims.role || claims.Role ||
          claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || null;
        if (role) sessionStorage.setItem('userRole', role);
      }

      sessionStorage.setItem('objOutput', JSON.stringify(rawUser));
      sessionStorage.setItem('userId', JSON.stringify(user.userId));
      sessionStorage.setItem('classId', JSON.stringify(user.classId));
      sessionStorage.setItem('sectionId', JSON.stringify(user.sectionId));
      sessionStorage.setItem('employeeId', JSON.stringify(user.employeeId));
      sessionStorage.setItem('userGroupId', JSON.stringify(user.userGroupId));
      sessionStorage.setItem('userType', JSON.stringify(user.userType));
      sessionStorage.setItem('organizationName', JSON.stringify(user.organizationName));
      sessionStorage.setItem('logo', JSON.stringify(user.logo));
      sessionStorage.setItem('clientImage', JSON.stringify(user.imageUrl));
      sessionStorage.setItem('clientName', JSON.stringify(user.clientName));
      sessionStorage.setItem('expirydate', JSON.stringify(user.expiryDate));
      sessionStorage.setItem('branchId', JSON.stringify(user.branchId));
      sessionStorage.setItem('is_Master', JSON.stringify(user.is_Master));

      await fetchSchoolDetails(apiUrl);

      if (user.userType === "Parent" && user.userGroupId === 7 && user.classId && user.sectionId) {
        await fetchTeacherDetails(apiUrl, user.classId, user.sectionId);
      }

      let redirectUrl = "/dashboard";
      if (isWaralsAdmin && (user.userType === "SuperAdmin" ||
          (user.userType === "Employee" && user.userGroupId === 1))) {
        redirectUrl = "/admin/tenants";
      } else if (user.userType === "Student") {
        redirectUrl = "/studentdashboard";
      } else if (user.userType === "Employee" && user.userGroupId === 5) {
        redirectUrl = "/teachersdashboard";
      } else if (user.userType === "Parent" && user.userGroupId === 7) {
        redirectUrl = "/ParentDashboard";
      }

      navigate(redirectUrl);
    } else {
      let errorMessage = 'Invalid credentials';
      try {
        const errorData = await response.json();
        if (errorData.msg) errorMessage = errorData.msg;
      } catch (parseError) {
        console.error("Failed to parse error response:", parseError);
      }
      console.error("Login failed:", errorMessage);
      setSnackbarMessage(errorMessage);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  } catch (error) {
    console.error("API request error:", error);
    setSnackbarMessage('An error occurred. Please try again later.');
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
  } finally {
    setLoading(false);
  }
};

  const logo = sessionStorage.getItem('clientLogo');
  const name = sessionStorage.getItem('clientCode');

  return (
    <div className="home">
      <main className="login-body" data-vide-bg="assets/img/login-bg.mp4">
        <form className="form-default" onSubmit={handlelogIn}>
          <div className="login-form">

            <div className="logo-login">
              <img src={`https://wsms.aidigitalsec.com${logo.replace('~', '')}`} alt="Logo" />
            </div>
            <h2>Login : {name}</h2>
            <div className="form-input">
              <label htmlFor="name">User Name</label>
              <input
                type="text"
                name="userId"
                placeholder="Username"
                onChange={handleInputChange}
                value={formData.userId}
              />
            </div>
            <div className="form-input" style={{ position: 'relative' }}>
              <label htmlFor="password">Password</label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                onChange={handleInputChange}
                value={formData.password}
                style={{ paddingRight: '40px' }}
              />
              <IconButton
                onClick={handleTogglePasswordVisibility}
                edge="end"
                style={{
                  position: 'absolute',
                  right: '5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  marginTop: '12px'
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </div>
            <div className="form-input pt-30" style={{ position: 'relative' }}>
              <input type="submit" name="submit" value={loading ? '' : 'Login'} disabled={loading} />
              {loading && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  pointerEvents: 'none',
                  color: '#000000',
                  fontSize: '14px',
                }}>
                  <CircularProgress size={16} style={{ color: '#000000' }} />
                  Logging in...
                </div>
              )}
            </div>

          </div>
        </form>
      </main>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LogIn;
